// src/api/index.ts
import { useAuthStore } from '@/stores/auth'

// Базовый URL API (можно вынести в .env)
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''

// Типы для ответов API
export interface ApiResponse<T = any> {
  success: boolean
  data: T | null
  error: {
    code: string
    message: string
  } | null
  validationErrors: Array<{
    field: string
    message: string
  }> | null
  warning: string | null
}

// Типы для запросов
export interface ApiRequestOptions extends RequestInit {
  skipAuth?: boolean
}

// ✅ Создание заголовков с JWT токеном
function getHeaders(options: ApiRequestOptions = {}): HeadersInit {
  const headers: HeadersInit = {
    'Content-Type': 'application/json'
  }

  // Добавляем JWT токен если не указано skipAuth
  if (!options.skipAuth) {
    const authStore = useAuthStore()
    if (authStore.token) {
      headers['Authorization'] = `Bearer ${authStore.token}`
    }
  }

  // Merge с пользовательскими заголовками
  if (options.headers) {
    Object.assign(headers, options.headers)
  }

  return headers
}

// ✅ Базовый метод для запросов
async function request<T>(
  endpoint: string,
  options: ApiRequestOptions = {}
): Promise<ApiResponse<T>> {
  const url = `${API_BASE_URL}${endpoint}`

  try {
    const response = await fetch(url, {
      ...options,
      headers: getHeaders(options)
    })

    const data: ApiResponse<T> = await response.json()

    // ✅ ИСПРАВЛЕНО: Не делаем logout для login endpoint!
    // 401 от /api/auth/login = неверный логин/пароль (не сессия истекла)
    if (response.status === 401 && endpoint !== '/api/auth/login') {
      const authStore = useAuthStore()
      authStore.logout()
      window.location.href = '/login'
    }

    return data
  } catch (error) {
    console.error('API Error:', error)
    return {
      success: false,
      data: null,
      error: {
        code: 'NETWORK_ERROR',
        message: error instanceof Error ? error.message : 'Ошибка сети'
      },
      validationErrors: null,
      warning: null
    }
  }
}

// ✅ Методы для разных типов запросов
export const api = {
  // GET запрос
  get<T>(endpoint: string, options?: ApiRequestOptions): Promise<ApiResponse<T>> {
    return request<T>(endpoint, { ...options, method: 'GET' })
  },

  // POST запрос
  post<T>(endpoint: string, body?: any, options?: ApiRequestOptions): Promise<ApiResponse<T>> {
    return request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined
    })
  },

  // PUT запрос
  put<T>(endpoint: string, body?: any, options?: ApiRequestOptions): Promise<ApiResponse<T>> {
    return request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined
    })
  },

  // DELETE запрос
  delete<T>(endpoint: string, options?: ApiRequestOptions): Promise<ApiResponse<T>> {
    return request<T>(endpoint, { ...options, method: 'DELETE' })
  }
}

export default api